import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { webOrigin } from '../utils/brand';
import { useLocation, useNavigate, useParams, useSearchParams } from 'react-router-dom';
import { useUser } from '../context/UserContext';
import { useAuth } from '../context/AuthContext';
import { useDialog } from '../context/DialogContext';
import { useBackground } from '../context/BackgroundContext';
import useIsMobile from '../hooks/useIsMobile';
import useInfiniteScroll from '../hooks/useInfiniteScroll';
import useImageRatios from '../hooks/useImageRatios';
import useMediaQuery from '../utils/useMediaQuery';
import { SurfacePage, SoftSurface, StickyCtaBar } from '../components/surface';
import LoadingSpinner from '../components/LoadingSpinner';
import { ErrorState } from '../components/EmptyState';
import ClubShareSheet from '../components/ClubShareSheet';
import ClubMembershipCheckoutModal from '../components/ClubMembershipCheckoutModal';
import ProCoverEditor from '../components/pro/ProCoverEditor';
import { resolveLayout } from '../components/event/coverLayout';
import ClubCover, { ClubLogo, ClubPills, MemberFaces } from '../components/club/ClubCover';
import { coverFromClub, coverSlice, clubCoverApi, CLUB_COVER_COPY } from '../components/club/clubCoverModel';
import { ClubTopBar, PreviewBand, PendingBand, ClubCtas, ClubTabs, ClubIdentityCard, MobileStats } from '../components/club/ClubHeader';
import { PillButton, Toast, useToast, MUTED, BODY } from '../components/club/clubUi';
import ClubLockCard from '../components/club/ClubLockCard';
import ClubHomeTab from '../components/club/ClubHomeTab';
import ClubFeed from '../components/club/ClubFeed';
import ClubEventsTab, { splitEvents } from '../components/club/ClubEventsTab';
import ClubForumTab from '../components/club/ClubForumTab';
import ClubMembersTab from '../components/club/ClubMembersTab';
import ClubAboutTab, { AboutPanel, RitualPanel } from '../components/club/ClubAboutTab';
import ClubRail from '../components/club/ClubRail';
import { clubViewerState } from '../components/club/clubViewerState';
import { buildFeed, feedStatus, applyVote, postsLastWeek, PAGE, ANNOUNCEMENT_PAGE_SIZE } from '../components/club/clubFeedModel';
import clubService from '../services/clubService';
import clubMembershipService from '../services/clubMembershipService';
import gatheringService from '../services/gatheringService';
import signalService from '../services/signalService';
import notificationService from '../services/notificationService';
import { monthYearLabel, plural } from '../utils/surfaceFormat';

/**
 * /clubs/:id and /clubs/invite/:code — the club page on the Portal vocabulary
 * (design_handoff_club_pages/README.md §1): borderless white panels on #F5F5F5, pill controls,
 * round avatars, the rainbow ring on the active tab, and the same cover system the Pro page has
 * (photo pool, layouts, drag-to-reframe, card photo, cover video) for the club's admins.
 *
 * Top bar → PREVIEW / REQUESTED band → cover (+ the editor when open) → CTAs → tabs → the body
 * grid `minmax(0,1fr) 380px` with the sticky rail. Seven viewer variants come out of
 * `clubViewerState`; gating is client-side (the content lists are not server-gated), so a
 * locked club fetches nothing but the club itself and — when the lock is a fee, not privacy —
 * its public events. Admins keep the 15s pending-requests poll, PREVIEW AS MEMBER and every
 * moderation action; members keep reactions, replies, voting, mute, share and leave.
 * Destructive actions arm on the first tap and act on the second (no dialogs).
 */
const PENDING_REQUESTS_POLL_INTERVAL = 15000;
const TAB_KEYS = ['home', 'posts', 'events', 'forum', 'members', 'about'];

const list = (r) => (r?.success ? (r.data?.items || r.data || []) : null);

export default function ClubPage() {
  const navigate = useNavigate();
  const { id, code } = useParams();
  const { hash } = useLocation();
  const [params, setParams] = useSearchParams();
  const { profile } = useUser();
  const { user } = useAuth();
  const { showAlert, showConfirm } = useDialog();
  const { setOverrideUrl } = useBackground();
  const mobile = useIsMobile();
  // The rail column needs 1040px (README §1 "Body"); jsdom has no matchMedia, so it reads as wide.
  const wideQuery = useMediaQuery('(min-width: 1040px)');
  const wide = !mobile && (wideQuery || (typeof window !== 'undefined' && typeof window.matchMedia !== 'function'));
  const { toast, say } = useToast();

  const [club, setClub] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);

  const [isInviteView, setIsInviteView] = useState(Boolean(code));
  const [isJoining, setIsJoining] = useState(false);
  const [joinError, setJoinError] = useState(null);

  const [announcements, setAnnouncements] = useState(null);
  const [annPage, setAnnPage] = useState(1);
  const [annHasMore, setAnnHasMore] = useState(false);
  const [annLoading, setAnnLoading] = useState(false);
  const [polls, setPolls] = useState([]);
  const [events, setEvents] = useState([]);
  const [showHidden, setShowHidden] = useState(false);
  const [gatherings, setGatherings] = useState([]);
  const [members, setMembers] = useState([]);
  const [forum, setForum] = useState(null);
  const [forumFiltered, setForumFiltered] = useState(null);
  const [forumCategory, setForumCategory] = useState(null);
  const [forumLoading, setForumLoading] = useState(false);
  const [signals, setSignals] = useState([]);
  const [signalsLoading, setSignalsLoading] = useState(false);
  const [pendingRequests, setPendingRequests] = useState([]);
  const [blockedUsers, setBlockedUsers] = useState([]);
  const [myMembership, setMyMembership] = useState(null);
  const [ownerStats, setOwnerStats] = useState(null);
  // Every loader below reads `if (r.success) setX(...)`. Without this, a failed request left the
  // state at its initial [] and the panel rendered its EMPTY copy — a 500 and an empty club were
  // the same picture. `mark` records the failure per resource; `retry` re-runs the effects.
  const [loadErrors, setLoadErrors] = useState({});
  const [retryNonce, setRetryNonce] = useState(0);
  const mark = useCallback((key) => (r) => {
    const failed = !r?.success;
    setLoadErrors((e) => (!!e[key] === failed ? e : { ...e, [key]: failed }));
    return r;
  }, []);
  const retry = useCallback(() => setRetryNonce((n) => n + 1), []);

  const [previewAsMember, setPreviewAsMember] = useState(false);
  const [clubMuted, setClubMuted] = useState(false);
  const [showInviteModal, setShowInviteModal] = useState(false);
  const [paidCheckoutOpen, setPaidCheckoutOpen] = useState(false);
  const [memberBusy, setMemberBusy] = useState(null);
  const [linkBusy, setLinkBusy] = useState(false);
  const [linkError, setLinkError] = useState(null);
  const [revealed, setRevealed] = useState(PAGE);
  const [composeKey, setComposeKey] = useState(0);

  const currentUserId = profile?.id || profile?.userId || user?.id;
  const tab = TAB_KEYS.includes(params.get('tab')) ? params.get('tab') : 'home';
  const setTab = useCallback((t) => {
    const n = new URLSearchParams(params);
    if (t === 'home') n.delete('tab'); else n.set('tab', t);
    setParams(n, { replace: true });
  }, [params, setParams]);

  const state = useMemo(() => clubViewerState(club, {
    previewAsMember, postCount: announcements ? announcements.length : null, membership: myMembership, ownerStats,
  }), [club, previewAsMember, announcements, myMembership, ownerStats]);
  const { isAdmin, isActualAdmin, isOwner, isMember, locked } = state;

  // ── The cover document: photos in order with framing, layout, split, count, card ──────────
  const [editOpen, setEditOpen] = useState(false);
  const [cover, setCover] = useState(() => coverFromClub(club));
  const [coverFor, setCoverFor] = useState(club);
  const [videoOverride, setVideoOverride] = useState(undefined);
  // Re-derived during render (not in an effect) so the first paint already has the photos.
  if (club !== coverFor) { setCoverFor(club); setCover(coverFromClub(club)); setVideoOverride(undefined); }
  const ratios = useImageRatios(cover.photos.map((p) => p.src));
  const shapedCover = useMemo(() => ({ ...cover, photos: cover.photos.map((p) => ({ ...p, r: ratios[p.src] || p.r, measured: !!ratios[p.src] })) }), [cover, ratios]);
  const coverPhotos = useMemo(() => coverSlice(shapedCover), [shapedCover]);
  const layoutKey = resolveLayout(shapedCover.layout, coverPhotos);
  const video = videoOverride === undefined ? club?.coverVideo : videoOverride;
  const coverApi = useMemo(() => (club?.id ? clubCoverApi(club.id) : null), [club?.id]);
  // Drag-to-reframe moves the focal point locally per pointer move; the server gets one PUT
  // once the hand has rested for a moment.
  const focalTimer = useRef(null);
  const focalPending = useRef({});
  const clubIdRef = useRef(null);
  clubIdRef.current = club?.id || null;
  useEffect(() => () => window.clearTimeout(focalTimer.current), []);
  const onFocal = useCallback((pid, fx, fy) => {
    setCover((c) => ({ ...c, photos: c.photos.map((p) => (p.id === pid ? { ...p, fx, fy } : p)) }));
    focalPending.current[pid] = { id: pid, x: Math.round(fx), y: Math.round(fy) };
    window.clearTimeout(focalTimer.current);
    focalTimer.current = window.setTimeout(() => {
      const focal = Object.values(focalPending.current);
      focalPending.current = {};
      if (clubIdRef.current && focal.length) clubService.updateCover(clubIdRef.current, { focal });
    }, 600);
  }, []);
  // Settings › BASICS deep-links `/clubs/:id#edit-cover` to open the editor.
  useEffect(() => { if (hash === '#edit-cover' && isActualAdmin) setEditOpen(true); }, [hash, isActualAdmin]);

  // ── Club ──────────────────────────────────────────────────────────────────────
  const fetchClub = useCallback(async () => {
    const result = code ? await clubService.getClubByInviteCode(code) : await clubService.getClub(id);
    if (result.success) {
      setClub(result.data);
      if (code && result.data?.userRole) setIsInviteView(false);
      setError(null);
      setClosed(null);
    } else {
      // A 404 on a club id may be a soft-deleted club: its old URL says so, and the
      // owner gets RESTORE for the window.
      if (id && !result.isNetwork) {
        const c = await clubService.getClosed(id);
        if (c.success) { setClosed(c.data); setError(null); setIsLoading(false); return; }
      }
      setError(result.error || 'Club not found');
    }
    setIsLoading(false);
  }, [id, code]);

  const [closed, setClosed] = useState(null);
  const [restoring, setRestoring] = useState(false);
  const restoreClub = async () => {
    setRestoring(true);
    const r = await clubService.restoreClub(id);
    setRestoring(false);
    if (r.success) { setClosed(null); setIsLoading(true); fetchClub(); }
    else await showAlert({ title: 'COULD NOT RESTORE', message: r.error || 'Try again.', type: 'error' });
  };

  useEffect(() => { setIsLoading(true); fetchClub(); }, [fetchClub]);

  useEffect(() => {
    setOverrideUrl(club?.backgroundUrl || null);
    return () => setOverrideUrl(null);
  }, [club?.backgroundUrl, setOverrideUrl]);

  // ── Content (client-gated: a locked club fetches only what the lock allows) ─────
  const clubId = club?.id;
  const contentOpen = !!clubId && !isInviteView && !locked;
  const eventsOpen = !!clubId && !isInviteView && state.eventsOpen;

  const loadAnnouncements = useCallback(async (page) => {
    setAnnLoading(true);
    const r = await clubService.getAnnouncements(clubId, page, ANNOUNCEMENT_PAGE_SIZE);
    const items = list(r) || [];
    setAnnouncements((prev) => (page === 1 || !prev ? items : [...prev, ...items.filter((a) => !prev.some((p) => p.id === a.id))]));
    setAnnPage(page);
    setAnnHasMore(items.length >= ANNOUNCEMENT_PAGE_SIZE);
    setAnnLoading(false);
  }, [clubId]);

  useEffect(() => {
    if (!contentOpen) { setAnnouncements(clubId && !isInviteView ? [] : null); return; }
    loadAnnouncements(1);
    clubService.getPolls(clubId).then(mark('polls')).then((r) => { const l = list(r); if (l) setPolls(l); });
    clubService.getMembers(clubId).then(mark('members')).then((r) => { const l = list(r); if (l) setMembers(l); });
  }, [contentOpen, clubId, isInviteView, loadAnnouncements, mark, retryNonce]);

  useEffect(() => {
    if (!eventsOpen) return;
    clubService.getClubEvents(clubId, 1, 20, showHidden).then(mark('events')).then((r) => { const l = list(r); if (l) setEvents(l); });
  }, [eventsOpen, clubId, showHidden, mark, retryNonce]);

  useEffect(() => {
    if (!eventsOpen || club?.gatheringsEnabled === false) return;
    gatheringService.listForClub(clubId).then(mark('gatherings')).then((r) => { if (r.success) setGatherings(r.data || []); });
  }, [eventsOpen, clubId, club?.gatheringsEnabled, mark, retryNonce]);

  useEffect(() => {
    if (!contentOpen || !club?.forumEnabled) return;
    clubService.getForumOverview(clubId, null).then(mark('forum')).then((r) => { if (r.success) setForum(r.data); });
  }, [contentOpen, clubId, club?.forumEnabled, mark, retryNonce]);

  useEffect(() => {
    if (!contentOpen || !club?.forumEnabled || !forumCategory) { setForumFiltered(null); return; }
    setForumLoading(true);
    clubService.getForumOverview(clubId, forumCategory).then((r) => { if (r.success) setForumFiltered(r.data); setForumLoading(false); });
  }, [contentOpen, clubId, club?.forumEnabled, forumCategory]);

  const signalsOn = contentOpen && isMember && club?.memberSignalsEnabled !== false;
  useEffect(() => {
    if (!signalsOn) { setSignals([]); return; }
    setSignalsLoading(true);
    signalService.getClubSignals(clubId).then(mark('signals')).then((r) => { if (r.success) setSignals(r.data || []); setSignalsLoading(false); });
  }, [signalsOn, clubId, mark, retryNonce]);

  // Admin-only: pending requests + blocked users, then the 15s poll for new requests.
  useEffect(() => {
    if (!clubId || isInviteView || !isActualAdmin) return undefined;
    Promise.all([clubService.getPendingJoinRequests(clubId), clubService.getBlockedUsers(clubId)]).then(([p, b]) => {
      const pl = list(p); if (pl) setPendingRequests(pl);
      const bl = list(b); if (bl) setBlockedUsers(bl);
    });
    const t = setInterval(async () => {
      const r = await clubService.getPendingJoinRequests(clubId);
      const l = list(r); if (l) setPendingRequests(l);
    }, PENDING_REQUESTS_POLL_INTERVAL);
    return () => clearInterval(t);
  }, [clubId, isInviteView, isActualAdmin]);

  useEffect(() => {
    if (!clubId || isInviteView || !isActualAdmin) return;
    clubMembershipService.getOwnerStats(clubId).then((r) => { if (r.success) setOwnerStats(r.data); });
  }, [clubId, isInviteView, isActualAdmin]);

  useEffect(() => {
    if (!clubId || !isMember) { setMyMembership(null); return; }
    clubService.getMyMembership(clubId).then((r) => { if (r.success) setMyMembership(r.data || null); });
  }, [clubId, isMember]);

  useEffect(() => {
    if (!clubId || !isMember) { setClubMuted(false); return undefined; }
    let done = false;
    notificationService.getMutes().then((r) => {
      if (done || !r?.success) return;
      const l = r.data?.items || r.data || [];
      setClubMuted(l.some((m) => m.targetType === 'club' && m.targetId === clubId));
    });
    return () => { done = true; };
  }, [clubId, isMember]);

  // ── Feed ──────────────────────────────────────────────────────────────────────
  const feedItems = useMemo(() => buildFeed({
    announcements: announcements || [], polls, events, gatherings, threads: forum?.threads || [], signals, members, clubName: club?.name,
  }), [announcements, polls, events, gatherings, forum, signals, members, club?.name]);

  useEffect(() => { setRevealed(PAGE); }, [tab]);

  const loadMore = useCallback(() => {
    if (revealed < feedItems.length) setRevealed((n) => n + PAGE);
    else if (annHasMore && !annLoading) loadAnnouncements(annPage + 1);
  }, [revealed, feedItems.length, annHasMore, annLoading, annPage, loadAnnouncements]);
  // `tab === 'posts'` is part of hasMore so the observer effect re-runs once the sentinel
  // has actually mounted (the hook only re-observes when its inputs change).
  const sentinelRef = useInfiniteScroll(loadMore, { hasMore: tab === 'posts' && (revealed < feedItems.length || annHasMore), loading: annLoading });
  const status = feedStatus({ revealed, total: feedItems.length, serverHasMore: annHasMore, loading: annLoading });

  // ── Membership actions ─────────────────────────────────────────────────────────
  const refreshMembers = useCallback(async () => {
    if (!clubId) return;
    const l = list(await clubService.getMembers(clubId)); if (l) setMembers(l);
  }, [clubId]);
  const refreshPending = useCallback(async () => {
    if (!clubId) return;
    const l = list(await clubService.getPendingJoinRequests(clubId)); if (l) setPendingRequests(l);
  }, [clubId]);
  const refreshBlocked = useCallback(async () => {
    if (!clubId) return;
    const l = list(await clubService.getBlockedUsers(clubId)); if (l) setBlockedUsers(l);
  }, [clubId]);
  const refreshEvents = useCallback(async () => {
    if (!clubId) return;
    const l = list(await clubService.getClubEvents(clubId, 1, 20, showHidden)); if (l) setEvents(l);
  }, [clubId, showHidden]);
  const refreshPolls = useCallback(async () => {
    if (!clubId) return;
    const l = list(await clubService.getPolls(clubId)); if (l) setPolls(l);
  }, [clubId]);

  const handleJoinByInvite = async () => {
    setIsJoining(true); setJoinError(null);
    const r = await clubService.joinByInviteCode(code);
    if (r.success) {
      setClub((prev) => (prev ? { ...prev, userRole: r.data?.role || 'member', hasPendingRequest: false, hasPendingInvite: false } : prev));
      setIsInviteView(false);
      await fetchClub();
    } else setJoinError(r.error || 'Failed to join club');
    setIsJoining(false);
  };

  const handleJoin = async () => {
    if (!club) return;
    if (club.membershipMode && club.membershipMode !== 'free' && !club.hasPendingInvite) { setPaidCheckoutOpen(true); return; }
    const r = await clubService.joinClub(club.id);
    if (!r.success && (r.error || '').includes('CLUB_REQUIRES_PAYMENT')) { setPaidCheckoutOpen(true); return; }
    if (r.success && r.data) {
      if (r.data.membership) {
        setClub((prev) => (prev ? { ...prev, userRole: r.data.membership.role, hasPendingRequest: false, hasPendingInvite: false, memberCount: (prev.memberCount || 0) + 1 } : prev));
      } else if (r.data.pending) {
        setClub((prev) => (prev ? { ...prev, hasPendingRequest: true, pendingRequestSentAt: r.data.request?.createdAt || new Date().toISOString() } : prev));
      }
      await fetchClub();
    } else if (!r.success) {
      await showAlert({ title: 'COULD NOT JOIN', message: r.error || 'Something went wrong.', type: 'error' });
    }
  };

  const handleLeave = async () => {
    if (isOwner) { await showAlert({ title: 'OWNERS STAY', message: 'Owners cannot leave their club. Transfer ownership first.', type: 'info' }); return; }
    const ok = await showConfirm({ title: 'LEAVE CLUB', message: 'Are you sure you want to leave this club?', confirmLabel: 'LEAVE' });
    if (!ok) return;
    const r = await clubService.leaveClub(club.id);
    if (r.success) { setPreviewAsMember(false); await fetchClub(); }
  };

  const handleWithdraw = async () => {
    const ok = await showConfirm({ title: 'WITHDRAW REQUEST', message: 'Withdraw your request to join? You can ask again later — this leaves no mark.', confirmLabel: 'WITHDRAW', cancelLabel: 'KEEP IT' });
    if (!ok) return;
    const r = await clubService.withdrawJoinRequest(club.id);
    if (r.success) {
      setClub((prev) => (prev ? { ...prev, hasPendingRequest: false, pendingRequestSentAt: null } : prev));
      await fetchClub();
    } else await showAlert({ title: 'COULD NOT WITHDRAW', message: r.error || 'Something went wrong.', type: 'error' });
  };

  const handleManageMembership = async () => {
    const m = myMembership;
    if (!m || m.subscriptionCanceledAt || m.paidStatus === 'canceled') { await handleLeave(); return; }
    const through = m.paidThroughAt ? new Date(m.paidThroughAt).toLocaleDateString('en-US', { month: 'short', day: 'numeric' }) : null;
    const ok = await showConfirm({
      title: 'CANCEL MEMBERSHIP',
      message: through ? `Cancel the renewal? You keep everything until ${through}, then you are out.` : 'Cancel your membership?',
      confirmLabel: 'CANCEL RENEWAL', cancelLabel: 'KEEP IT', variant: 'danger',
    });
    if (!ok) return;
    const r = await clubMembershipService.cancel(club.id, true);
    if (r.success) {
      const mm = await clubService.getMyMembership(club.id);
      if (mm.success) setMyMembership(mm.data || null);
      await fetchClub();
    } else await showAlert({ title: 'COULD NOT CANCEL', message: r.error || 'Something went wrong.', type: 'error' });
  };

  const toggleMute = async () => {
    const next = !clubMuted;
    setClubMuted(next);
    const r = next ? await notificationService.addMute('club', club.id) : await notificationService.removeMute('club', club.id);
    if (!r.success) setClubMuted(!next);
    else say(next ? 'CLUB MUTED' : 'NOTIFICATIONS BACK ON');
  };

  const share = async () => {
    if (isMember && club.inviteCode) { setShowInviteModal(true); return; }
    const url = `${webOrigin()}/clubs/${club.id}`;
    try {
      if (navigator.share) { await navigator.share({ title: club.name, url }); return; }
      await navigator.clipboard.writeText(url);
      await showAlert({ title: 'LINK COPIED', message: url, type: 'success' });
    } catch { /* user dismissed the share sheet */ }
  };

  const onAction = (action) => {
    switch (action) {
      case 'join': return handleJoin();
      case 'leave': return handleLeave();
      case 'withdraw': return handleWithdraw();
      case 'manage-membership': return handleManageMembership();
      case 'mute': return toggleMute();
      case 'share': return share();
      case 'invite': return isMember && club.inviteCode ? setShowInviteModal(true) : share();
      case 'post': setTab('posts'); setComposeKey((k) => k + 1); return undefined;
      case 'thread': return navigate(`/clubs/${club.id}/forum`);
      case 'signal': return navigate(`/portals/new?club=${club.id}`);
      case 'events': return setTab('events');
      case 'members': return setTab('members');
      case 'create-event': return navigate(`/host/events/create?clubId=${club.id}`);
      case 'manage': return navigate(`/clubs/${club.id}/manage`);
      case 'revenue': return navigate(`/clubs/${club.id}/revenue`);
      default: return undefined;
    }
  };

  // ── Announcements ─────────────────────────────────────────────────────────────
  const handlePost = async ({ content, isPinned }) => {
    const r = await clubService.createAnnouncement(club.id, { content, isPinned });
    if (r.success) { await loadAnnouncements(1); say('POSTED TO THE CLUB'); }
    return r;
  };
  const handleReact = async (a, emoji) => {
    const toggle = (ann) => ({
      ...ann,
      reactions: (() => {
        const cur = ann.reactions || [];
        const hit = cur.find((x) => x.emoji === emoji);
        if (!hit) return [...cur, { emoji, count: 1, userReacted: true }];
        return cur.map((x) => (x.emoji === emoji ? { ...x, count: Math.max(0, x.count + (x.userReacted ? -1 : 1)), userReacted: !x.userReacted } : x));
      })(),
    });
    setAnnouncements((prev) => prev?.map((x) => (x.id === a.id ? toggle(x) : x)) || prev);
    const r = await clubService.reactToAnnouncement(a.id, emoji);
    if (!r.success) setAnnouncements((prev) => prev?.map((x) => (x.id === a.id ? toggle(x) : x)) || prev);
  };
  const handleEdit = async (a, { content, isPinned }) => {
    const r = await clubService.updateAnnouncement(a.id, { content, isPinned });
    if (r.success) setAnnouncements((prev) => prev?.map((x) => (x.id === a.id ? { ...x, content, isPinned, updatedAt: new Date().toISOString() } : x)) || prev);
    else await showAlert({ title: 'SAVE FAILED', message: r.error || 'Could not save the post.', type: 'error' });
  };
  const handleTogglePin = async (a) => {
    const r = await clubService.updateAnnouncement(a.id, { content: a.content, isPinned: !a.isPinned });
    if (r.success) setAnnouncements((prev) => prev?.map((x) => (x.id === a.id ? { ...x, isPinned: !a.isPinned } : x)) || prev);
  };
  // DELETE arms on the card (CONFIRM DELETE) — the second tap lands here.
  const handleDelete = async (a) => {
    const r = await clubService.deleteAnnouncement(a.id);
    if (r.success) { setAnnouncements((prev) => prev?.filter((x) => x.id !== a.id) || prev); say('POST DELETED'); }
    else await showAlert({ title: 'DELETE FAILED', message: r.error || 'Could not delete the post.', type: 'error' });
  };

  // ── Polls ─────────────────────────────────────────────────────────────────────
  const handleCreatePoll = async ({ question, options, expiresAt }) => {
    const r = await clubService.createPoll(club.id, { question, options, expiresAt });
    if (r.success) { await refreshPolls(); say('POLL IS UP'); }
    return r;
  };
  const handleVote = async (pollId, optionIndex) => {
    const before = polls;
    setPolls((prev) => prev.map((p) => (p.id === pollId ? applyVote(p, optionIndex) : p)));
    const r = await clubService.voteOnPoll(pollId, optionIndex);
    if (r.success) await refreshPolls(); else setPolls(before);
  };
  const handleDeletePoll = async (poll) => {
    const r = await clubService.deletePoll(poll.id);
    if (r.success) { setPolls((prev) => prev.filter((p) => p.id !== poll.id)); say('POLL DELETED'); }
    else await showAlert({ title: 'DELETE FAILED', message: r.error || 'Failed to delete poll', type: 'error' });
  };

  // ── Events ────────────────────────────────────────────────────────────────────
  const handleHideEvent = async (e) => {
    const r = await clubService.setClubEventHidden(club.id, e.id, !e.isHiddenInClub);
    if (r.success) await refreshEvents();
  };
  // UNLINK arms on the row (UNLINK?) — the second tap lands here.
  const handleUnlinkEvent = async (e) => {
    const r = await clubService.unlinkEventFromClub(club.id, e.id);
    if (r.success) { await refreshEvents(); say('UNLINKED · THE HOST CAN RE-LINK IT'); }
    else await showAlert({ title: 'UNLINK FAILED', message: r.error || 'Failed to unlink event', type: 'error' });
  };
  const handleLinkEvent = async (eventId) => {
    setLinkBusy(true); setLinkError(null);
    const r = await clubService.linkEventToClub(club.id, eventId);
    setLinkBusy(false);
    if (r.success) { await refreshEvents(); say('LINKED TO THE CLUB'); return true; }
    setLinkError(r.error || 'Failed to link event');
    return false;
  };

  // ── Requests / members ────────────────────────────────────────────────────────
  const handleAccept = async (p) => {
    const r = await clubService.acceptJoinRequest(club.id, p.id);
    if (r.success) { await Promise.all([refreshPending(), refreshMembers(), fetchClub()]); say(`${(p.displayName || 'THEY').split(' ')[0].toUpperCase()} IS IN`); }
    else await showAlert({ title: 'ACCEPT FAILED', message: r.error || 'Failed to accept request', type: 'error' });
  };
  const handleDecline = async (p) => {
    const r = await clubService.declineJoinRequest(club.id, p.id);
    if (r.success) await refreshPending();
    else await showAlert({ title: 'DECLINE FAILED', message: r.error || 'Failed to decline request', type: 'error' });
  };
  // BLOCK arms on the row (CONFIRM BLOCK) — the second tap lands here.
  const handleBlockRequest = async (p) => {
    const r = await clubService.blockJoinRequestApplicant(club.id, p.id);
    if (r.success) await Promise.all([refreshPending(), refreshBlocked()]);
    else await showAlert({ title: 'BLOCK FAILED', message: r.error || 'Failed to block applicant', type: 'error' });
  };
  const handleUnblock = async (b) => {
    const r = await clubService.unblockUser(club.id, b.userId);
    if (r.success) await refreshBlocked();
  };
  // KICK and BLOCK arm in the MANAGE menu (CONFIRM KICK / CONFIRM BLOCK); role changes act at
  // once (they reverse in the same menu); handing the club over still asks, since it does not.
  const handleMemberAction = async (m, action) => {
    const name = m.displayName;
    const first = (name || 'THEY').split(' ')[0].toUpperCase();
    let r;
    setMemberBusy(m.userId);
    try {
      if (action === 'promote' || action === 'demote') {
        const admin = action === 'promote';
        r = await clubService.updateMemberRole(club.id, m.userId, admin ? 'admin' : 'member');
        if (r.success) { await refreshMembers(); say(admin ? `${first} IS AN ADMIN` : 'DEMOTED TO MEMBER'); }
      } else if (action === 'transfer') {
        const ok = await showConfirm({ title: 'TRANSFER OWNERSHIP', message: `Transfer ownership of this club to ${name}? You will become a regular admin. This cannot be undone without their cooperation.`, confirmLabel: 'TRANSFER' });
        if (!ok) return;
        r = await clubService.transferOwnership(club.id, m.userId);
        if (r.success) await Promise.all([refreshMembers(), fetchClub()]);
      } else if (action === 'kick') {
        r = await clubService.removeMember(club.id, m.userId, false);
        if (r.success) { await Promise.all([refreshMembers(), fetchClub()]); say(`${first} REMOVED`); }
      } else if (action === 'block') {
        r = await clubService.removeMember(club.id, m.userId, true);
        if (r.success) { await Promise.all([refreshMembers(), refreshBlocked(), fetchClub()]); say(`${first} BLOCKED`); }
      }
      if (r && !r.success) await showAlert({ title: 'ACTION FAILED', message: r.error || 'Something went wrong.', type: 'error' });
    } finally {
      setMemberBusy(null);
    }
  };

  // ── Render ────────────────────────────────────────────────────────────────────
  const frame = (children, { narrow } = {}) => (
    <SoftSurface>
      <SurfacePage bare showTopBarOnMobile>
        <main className="mx-auto flex w-full flex-col font-mono" style={{ maxWidth: narrow || 1280, padding: mobile ? '12px 12px 24px' : '24px 32px 72px', gap: mobile ? 10 : 16, boxSizing: 'border-box' }} data-testid={club ? (mobile ? 'club-mobile' : 'club-desktop') : undefined}>
          {children}
        </main>
        <Toast text={toast} phone={mobile} />
      </SurfacePage>
    </SoftSurface>
  );

  if (isLoading) return frame(<div className="py-24 text-center"><LoadingSpinner label="LOADING CLUB…" /></div>);
  if (closed) {
    const until = new Date(closed.restoreUntil);
    const daysLeft = Math.max(0, Math.ceil((until.getTime() - Date.now()) / 86400000));
    return frame(
      <section className="flex flex-col items-center bg-white text-center" style={{ borderRadius: 28, padding: mobile ? '28px 20px' : '36px 28px', gap: 14 }} data-testid="club-closed">
        <span style={{ opacity: 0.5 }}><ClubLogo club={{ ...closed, avatarUrl: closed.avatarUrl }} size={80} radius={20} fontSize={34} /></span>
        <span className="uppercase" style={{ fontSize: 11, fontWeight: 700, letterSpacing: '.2em', color: MUTED }}>THIS CLUB CLOSED</span>
        <h1 className="m-0" style={{ fontSize: 28, fontWeight: 700, lineHeight: 1.1, textTransform: 'none' }}>{closed.name}</h1>
        <p className="m-0" style={{ maxWidth: 440, fontSize: 13, lineHeight: 1.65, color: BODY }}>
          {closed.canRestore
            ? `You closed it on ${new Date(closed.deletedAt).toLocaleDateString('en-US', { month: 'short', day: 'numeric' })}. Members, posts, the forum and your contact list are all still here for ${daysLeft} more ${daysLeft === 1 ? 'day' : 'days'}. After that it is gone for good.`
            : 'The owner closed it. Its members, posts and events are no longer on Portal.'}
        </p>
        <span className="flex flex-wrap justify-center" style={{ gap: 8 }}>
          {closed.canRestore && <PillButton size={48} fontSize={11} onClick={restoreClub} disabled={restoring} data-testid="restore-club">{restoring ? 'RESTORING…' : 'BRING IT BACK'}</PillButton>}
          <PillButton variant="secondary" size={48} fontSize={11} to="/clubs">BACK TO CLUBS</PillButton>
        </span>
      </section>,
      { narrow: 640 },
    );
  }
  if (error || !club) {
    return frame(
      <section className="flex flex-col items-center bg-white text-center" style={{ borderRadius: 28, padding: 28, gap: 14 }}>
        <ErrorState message={error || 'Club not found'} onRetry={() => { setIsLoading(true); fetchClub(); }} />
        <PillButton variant="secondary" size={44} fontSize={11} to="/clubs">BACK TO CLUBS</PillButton>
      </section>,
      { narrow: 640 },
    );
  }

  const since = monthYearLabel(club.createdAt);
  const sinceShort = since.replace(/ (\d\d)(\d\d)$/, " '$2");
  const inviteBanner = coverPhotos[0]?.src || club.bannerUrl || null;

  // Invite-code landing (README §1 "Invite landing"): the 560px card with the banner, the
  // overlapping logo, INVITED, name, tagline, faces, who invited you and one JOIN CLUB.
  if (isInviteView && !isMember) {
    return frame(
      <section className="flex flex-col overflow-hidden bg-white" style={{ borderRadius: 28 }} data-testid="invite-landing">
        <div style={{ height: 150, background: inviteBanner ? '#111' : '#F2F2F2' }}>
          {inviteBanner && <img src={inviteBanner} alt="" referrerPolicy="no-referrer" style={{ width: '100%', height: '100%', objectFit: 'cover', display: 'block' }} />}
        </div>
        <div className="flex flex-col items-center text-center" style={{ padding: '0 28px 30px', gap: 14 }}>
          <span style={{ marginTop: -48, border: '4px solid #fff', borderRadius: 22, background: '#fff', boxSizing: 'border-box' }}><ClubLogo club={club} size={88} radius={18} fontSize={36} /></span>
          <ClubPills club={club} state={{ pill: { label: 'INVITED', tone: 'cyan' } }} />
          <h1 className="m-0" style={{ fontSize: 30, fontWeight: 700, lineHeight: 1.1, textTransform: 'none' }}>{club.name}</h1>
          {(club.tagline || club.description) && <p className="m-0" style={{ fontFamily: '"Times New Roman", Times, serif', fontStyle: 'italic', fontSize: 19, lineHeight: 1.4 }}>{club.tagline || club.description}</p>}
          <MemberFaces members={members} total={club.memberCount ?? 0} since={since} size={32} />
          <span className="flex items-center" style={{ gap: 10, padding: '10px 18px 10px 14px', borderRadius: 999, background: '#F5F5F5', fontSize: 12 }}>You&rsquo;ve been invited to join this club.{club.membershipMode && club.membershipMode !== 'free' ? ` Membership is ${state.price}.` : ''}</span>
          {joinError && <p className="m-0" style={{ fontSize: 12, color: '#DC2626' }} role="alert">{joinError}</p>}
          <PillButton size={56} fontSize={12} tracking=".16em" style={{ width: '100%' }} onClick={handleJoinByInvite} disabled={isJoining} data-testid="invite-join">{isJoining ? 'JOINING…' : 'JOIN CLUB'}</PillButton>
          <span style={{ fontSize: 11, color: MUTED }}>{club.isPublic === false || club.requiresApproval ? 'An invite skips the review. You’re in the moment you tap.' : 'No review. You’re in the moment you tap.'}</span>
        </div>
      </section>,
      { narrow: 560 },
    );
  }

  const { upcoming } = splitEvents(events);
  const draftGatherings = gatherings.filter((g) => g.status === 'draft').length;
  const showForum = !!club.forumEnabled;
  const showMembers = club.showMembersList !== false || isAdmin;
  const forumCount = forum?.totalThreadCount ?? forum?.threads?.length;
  const tabs = [
    { key: 'home', label: 'HOME' },
    { key: 'posts', label: 'POSTS', gated: locked },
    { key: 'events', label: 'EVENTS', count: state.eventsOpen && upcoming.length > 0 ? upcoming.length : undefined, gated: !state.eventsOpen },
    ...(showForum ? [{ key: 'forum', label: 'FORUM', count: !locked && forumCount ? forumCount : undefined, gated: locked }] : []),
    ...(showMembers ? [{ key: 'members', label: 'MEMBERS', count: isAdmin && pendingRequests.length > 0 ? pendingRequests.length : undefined, alert: isAdmin, gated: locked }] : []),
    { key: 'about', label: 'ABOUT' },
  ];
  const tabGated = (key) => tabs.find((t) => t.key === key)?.gated;
  const postsPerWeek = club.postsLast7Days ?? postsLastWeek(announcements || []);
  const needs = [
    ...(pendingRequests.length > 0 ? [{ label: `${plural(pendingRequests.length, 'JOIN REQUEST')}`, count: pendingRequests.length, action: 'members' }] : []),
    ...(draftGatherings > 0 ? [{ label: `${plural(draftGatherings, 'DRAFT GATHERING')}`, count: draftGatherings, action: 'events' }] : []),
  ];
  const showSignals = isMember && club.memberSignalsEnabled !== false && !locked;
  const feedLoading = announcements === null || annLoading;

  const postHandlers = {
    onReact: handleReact, onEdit: handleEdit, onDelete: handleDelete, onTogglePin: handleTogglePin,
    onDeletePoll: handleDeletePoll, onVote: handleVote, onHideEvent: handleHideEvent, onUnlinkEvent: handleUnlinkEvent,
    onWriteFirst: () => { setComposeKey((k) => k + 1); },
  };

  const renderTab = () => {
    const gatedHere = tabGated(tab) && tab !== 'home';
    if (gatedHere) return <ClubLockCard lock={state.lock} onAction={onAction} mobile={mobile} />;
    switch (tab) {
      case 'posts':
        return <ClubFeed club={club} state={state} profile={profile} items={feedItems} revealed={revealed} status={status} sentinelRef={sentinelRef} loading={feedLoading && feedItems.length === 0} onPost={handlePost} onCreatePoll={handleCreatePoll} composeFocusKey={composeKey} mobile={mobile} postHandlers={postHandlers} />;
      case 'events':
        return <ClubEventsTab club={club} state={state} events={events} gatherings={gatherings} loadErrors={loadErrors} onRetryLoad={retry} showHidden={showHidden} onToggleShowHidden={() => setShowHidden((v) => !v)} onHide={handleHideEvent} onUnlink={handleUnlinkEvent} onLink={handleLinkEvent} linkBusy={linkBusy} linkError={linkError} mobile={mobile} />;
      case 'forum':
        return <ClubForumTab club={club} state={state} forum={forumCategory ? forumFiltered || forum : forum} loading={forumLoading} loadError={!!loadErrors.forum} onRetryLoad={retry} categoryId={forumCategory} onCategory={setForumCategory} mobile={mobile} />;
      case 'members':
        return <ClubMembersTab club={club} state={state} members={members} membersError={!!loadErrors.members} onRetryLoad={retry} pendingRequests={pendingRequests} blockedUsers={blockedUsers} currentUserId={currentUserId} busyUserId={memberBusy} onAccept={handleAccept} onDecline={handleDecline} onBlockRequest={handleBlockRequest} onUnblock={handleUnblock} onMemberAction={handleMemberAction} onInvite={() => onAction('invite')} mobile={mobile} />;
      case 'about':
        return <ClubAboutTab club={club} forumCategoryCount={forum?.categories?.length ?? null} mobile={mobile} canSeeSettings={!!state?.isAdmin} />;
      case 'home':
      default:
        if (locked) {
          return (
            <div className="flex flex-col" style={{ gap: mobile ? 10 : 16 }}>
              <ClubLockCard lock={state.lock} onAction={onAction} mobile={mobile} />
              <AboutPanel club={club} phone={mobile} />
              <RitualPanel club={club} phone={mobile} />
            </div>
          );
        }
        return <ClubHomeTab club={club} state={state} announcements={announcements || []} events={events} polls={polls} members={members} onVote={handleVote} onGoTab={(t, opts) => { setTab(t); if (opts?.compose) setComposeKey((k) => k + 1); }} mobile={mobile} />;
    }
  };

  const rail = (
    <ClubRail club={club} state={state} needs={needs} postsPerWeek={postsPerWeek} signals={signals} signalsLoading={signalsLoading} signalsError={!!loadErrors.signals} onRetryLoad={retry} showSignals={showSignals} muted={clubMuted} onAction={onAction} mobile={mobile} />
  );

  const topBar = <ClubTopBar state={state} previewAsMember={previewAsMember} onTogglePreview={() => setPreviewAsMember((v) => !v)} onAction={onAction} phone={mobile} />;
  const bands = (
    <>
      {previewAsMember && <PreviewBand onExit={() => setPreviewAsMember(false)} />}
      <PendingBand band={state.band} />
    </>
  );
  const coverNode = (
    <ClubCover club={club} state={state} members={members} memberCount={club.memberCount} since={since} photos={coverPhotos} layout={layoutKey} split={shapedCover.split} video={video}
      phone={mobile} admin={isAdmin} editing={isAdmin && editOpen} onToggleEdit={() => setEditOpen((o) => !o)} onFocal={onFocal} onOpenMembers={() => setTab('members')} />
  );
  const editor = isAdmin && editOpen && (
    <ProCoverEditor pageId={club.id} api={coverApi} copy={CLUB_COVER_COPY} canRemove testId="club-cover-editor" cover={shapedCover} setCover={setCover} video={video} onVideoChange={setVideoOverride} onReload={() => fetchClub()} onClose={() => setEditOpen(false)} phone={mobile} hasLogo={!!club.avatarUrl} />
  );
  const ctaRow = <ClubCtas ctas={state.ctas} onAction={onAction} muted={clubMuted} phone={mobile} />;
  const tabRow = <ClubTabs tabs={tabs} active={tab} onChange={setTab} phone={mobile} />;
  // Below 1040 the page is one column; on HOME (unlocked) the rail panels append after the digest.
  const body = wide ? (
    <div className="grid items-start" style={{ gridTemplateColumns: 'minmax(0, 1fr) 380px', gap: 16 }}>
      <div className="flex min-w-0 flex-col" style={{ gap: 16 }} data-testid="club-tab-content">{renderTab()}</div>
      <div className="flex min-w-0 flex-col" style={{ gap: 12, position: 'sticky', top: 88 }} data-testid="club-rail-frame">{rail}</div>
    </div>
  ) : (
    <div className="flex min-w-0 flex-col" style={{ gap: mobile ? 10 : 16 }} data-testid="club-tab-content">
      {renderTab()}
      {tab === 'home' && !locked && rail}
    </div>
  );
  const modals = (
    <>
      <ClubShareSheet isOpen={showInviteModal} onClose={() => setShowInviteModal(false)} club={club} clubService={clubService} mobile={mobile} />
      {paidCheckoutOpen && (
        <ClubMembershipCheckoutModal club={club} mobile={mobile} onClose={() => setPaidCheckoutOpen(false)} onSuccess={async () => { setPaidCheckoutOpen(false); await fetchClub(); say(`PAID · YOU’RE IN`); }} />
      )}
    </>
  );

  if (mobile) {
    const stats = [
      { n: club.memberCount ?? 0, k: 'MEMBERS' },
      { n: club.eventCount ?? 0, k: 'EVENTS' },
      { n: sinceShort, k: 'SINCE' },
    ];
    return frame(
      <>
        {topBar}
        {bands}
        {coverNode}
        <ClubIdentityCard club={club} state={state} />
        <MobileStats stats={stats} />
        {editor}
        {ctaRow}
        {tabRow}
        {body}
        {state.mobBar && (
          <StickyCtaBar price={state.mobBar.price} note={state.mobBar.note} cta={state.mobBar.cta} onCta={() => onAction(state.mobBar.action)} testId="club-cta-bar" />
        )}
        {modals}
      </>,
    );
  }

  return frame(
    <>
      {topBar}
      {bands}
      {coverNode}
      {editor}
      {ctaRow}
      {tabRow}
      {body}
      {modals}
    </>,
  );
}
